import React, { Component, Fragment } from "react";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import _ from "lodash";

import { withLayout } from "../../components/layout/Layout";
import { FormSubmitButton, Form } from "../../components/form";
import { Checkbox } from "../../components/checkbox/Checkbox";
import TimezonePicker from "./components/timezonePicker/TimezonePicker";
import LanguagePicker from "./components/languagePicker/LanguagePicker";
import { Notificator } from "../../components/notification";
import { SectionHeader } from "../profiles/components/sectionHeader/SectionHeader";
import {
  changeLanguage,
  changeTimeFormat,
  changeTimezone,
  getTimezones,
  changeAPRSetting,
} from "../../store/attributes/attributes.actions";
import { remap } from "../../utils/remap";
import { AriaPageTitle } from "../../components/ariaPageTitle";
import { isBypassPrivateDNSFeatureEnabled } from "../../store/settings/settings.selectors";
import { Desktop } from "../../hocs/Responsive";
import { withPageName } from "../../components/layout/horizontalMenu/PageName";

import "./AccountSettingsPage.scss";

class AccountSettingsPage extends Component {
  state = {
    selectedTimezone: null,
    is24hourFormat: false,
    aprOn: false,
  };

  componentDidMount() {
    this.props.getTimezones();
    const { accountSettings } = this.props;
    const selectedLanguage = accountSettings.language;
    const selectedTimezone = accountSettings.timezone;
    const is24hourFormat = !!accountSettings.is24hourFormat;
    const { aprOn } = accountSettings;

    this.setState({
      selectedLanguage,
      selectedTimezone,
      is24hourFormat,
      aprOn,
    });
  }

  componentDidUpdate(prevProps) {
    const { timezone, is24hourFormat, language, aprOn } =
      this.props.accountSettings;

    if (is24hourFormat !== prevProps.accountSettings.is24hourFormat) {
      this.setState({ is24hourFormat });
    }

    if (timezone !== prevProps.accountSettings.timezone) {
      this.setState({ selectedTimezone: timezone });
    }

    if (!this.state.selectedLanguage && language) {
      this.setState({ selectedLanguage: language });
    }

    if (aprOn !== prevProps.accountSettings.aprOn) {
      this.setState({ aprOn });
    }
  }

  handleTimezoneChange = (selectedTimezone) => {
    this.setState({ selectedTimezone });
  };

  handleLanguageChange = (selectedLanguage) => {
    this.setState({ selectedLanguage });
  };

  handleTimeFormatChange = () => {
    this.setState({
      is24hourFormat: !this.state.is24hourFormat,
    });
  };
  handleAPRChange = () => {
    this.setState({ aprOn: !this.state.aprOn });
  };

  save = () => {
    let promise = Promise.resolve();
    const {
      changeTimezone,
      changeLanguage,
      changeTimeFormat,
      changeAPRSetting,
    } = this.props;
    const { selectedTimezone, selectedLanguage, is24hourFormat, aprOn } =
      this.state;

    if (this.isTimezoneChanged()) {
      promise = promise.then(() => changeTimezone(selectedTimezone));
    }
    if (this.isLanguageChanged()) {
      promise = promise.then(() => changeLanguage(selectedLanguage));
    }
    if (this.isFormatChanged()) {
      promise = promise.then(() => changeTimeFormat(is24hourFormat));
    }
    if (this.isAPRChanged()) {
      promise = promise.then(() => changeAPRSetting(aprOn));
    }

    return promise;
  };

  isTimezoneChanged = () =>
    this.props.accountSettings.timezone !== this.state.selectedTimezone;
  isLanguageChanged = () =>
    this.props.accountSettings.language !== this.state.selectedLanguage;
  isFormatChanged = () =>
    !!this.props.accountSettings.is24hourFormat !== this.state.is24hourFormat;
  isAPRChanged = () => this.props.accountSettings.aprOn !== this.state.aprOn;

  isFormChanged = () =>
    this.isTimezoneChanged() ||
    this.isLanguageChanged() ||
    this.isFormatChanged() ||
    this.isAPRChanged();

  render() {
    const { isBypassPrivateDNSFeatureEnabled, languages } = this.props;
    const isMultilingual = _.size(languages) > 1;

    return (
      <section className="user-settings-page row">
        <AriaPageTitle pageKey="page_title_account_settings_page" />
        <div className="col-12 col-md-6">
          <Desktop>
            <h1 className="user-settings-page__header">
              <FormattedMessage id={"account"} />
            </h1>
          </Desktop>
          <Form
            onSubmit={this.save}
            name={"accountSettingsForm"}
            description={
              <FormattedMessage id={"account_settings_form_description"} />
            }
            hiddenDescription
          >
            <section className="time-section">
              <SectionHeader
                description={<FormattedMessage id={"timezone_description"} />}
              >
                <h2>
                  <FormattedMessage id={"time"} />
                </h2>
              </SectionHeader>

              <TimezonePicker
                onChange={this.handleTimezoneChange}
                selected={this.state.selectedTimezone}
                timezones={this.props.accountSettings.timezones}
              />

              <Checkbox
                id={"24_hour_format"}
                onChange={this.handleTimeFormatChange}
                checked={this.state.is24hourFormat}
                label={<FormattedMessage id={"24_hour_format"} />}
              />
            </section>

            <section className="language-section">
              {isMultilingual ? (
                <Fragment>
                  <SectionHeader>
                    <h2>
                      <FormattedMessage id={"language"} />
                    </h2>
                  </SectionHeader>

                  <LanguagePicker
                    languages={languages}
                    selected={this.state.selectedLanguage}
                    onChange={this.handleLanguageChange}
                  />
                </Fragment>
              ) : (
                <SectionHeader
                  description={
                    <FormattedMessage
                      id={"account_settings_language_description"}
                      values={{
                        language_name: (
                          <span className="text-emphasized">
                            {languages[0].name}
                          </span>
                        ),
                      }}
                    />
                  }
                >
                  <h2>
                    <FormattedMessage id={"language"} />
                  </h2>
                </SectionHeader>
              )}
            </section>

            {isBypassPrivateDNSFeatureEnabled && (
              <section className="apr-section">
                <SectionHeader
                  description={
                    <FormattedMessage
                      id={"privacy_account_section_description"}
                    />
                  }
                >
                  <h2>
                    <FormattedMessage id={"privacy_account_section_title"} />
                  </h2>
                </SectionHeader>

                <Checkbox
                  id={"apr_on"}
                  onChange={this.handleAPRChange}
                  checked={this.state.aprOn}
                  label={<FormattedMessage id={"apr_checkbox_account"} />}
                />
              </section>
            )}

            <FormSubmitButton
              label={<FormattedMessage id={"save"} />}
              disabled={!this.isFormChanged()}
            />
          </Form>
        </div>
      </section>
    );
  }
}

export default _.flowRight(
  withLayout(),
  withPageName(),
  connect(
    (state) => ({
      languages: _.map(state.config.languages.list, (language) =>
        remap(language, { key: "value" })
      ),
      accountSettings: state.accountSettings,
      isBypassPrivateDNSFeatureEnabled: isBypassPrivateDNSFeatureEnabled(state),
    }),
    {
      error: Notificator.error,
      changeLanguage,
      changeTimezone,
      changeTimeFormat,
      getTimezones,
      changeAPRSetting,
    }
  )
)(AccountSettingsPage);
